import { useAuth } from '@/components/providers';
import FirestoreService from '@/services/FirestoreService';
import { logDebug, logError, logInfo } from '@/utils/logger';
import { FEEDBACK } from '@/utils/strings';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
interface FeedbackContextType {
  isLoading: boolean;
  error: string | null;
  success: string | null;
  submitFeedback: (feedback: string) => Promise<void>;
  clearStatus: () => void;
}
const FeedbackContext = createContext<FeedbackContextType | undefined>(
  undefined,
);
export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return context;
};
export const FeedbackProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { user } = useAuth();
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);
  const submitFeedback = useCallback(
    async (feedback: string) => {
      if (!user) {
        setError(FEEDBACK.FORM.NOT_LOGGED_IN_ERROR);
        return;
      }
      setIsLoading(true);
      setError(null);
      setSuccess(null);
      try {
        await FirestoreService.submitFeedback(user, feedback);
        logInfo(
          'Feedback submitted successfully',
          'FeedbackProvider.submitFeedback',
        );
        setSuccess(FEEDBACK.FORM.SUCCESS_MESSAGE);
      } catch (error) {
        logError(error, 'FeedbackProvider.submitFeedback');
        setError(
          error instanceof Error ? error.message : FEEDBACK.FORM.GENERIC_ERROR,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [user],
  );
  const clearStatus = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);
  const value = useMemo(
    () => ({
      isLoading,
      error,
      success,
      submitFeedback,
      clearStatus,
    }),
    [isLoading, error, success, submitFeedback, clearStatus],
  );
  logDebug('FeedbackProvider render', 'FeedbackProvider', { isLoading });
  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
};
export default FeedbackProvider;
